import * as Blockly from 'blockly/core';

/**
 * Wraps an existing statement block (innerBlock) into a new block (wrapperBlock)
 * defined by its state. The inner block is inserted into the specified statement input
 * of the wrapper, and the wrapper takes the original position of the inner block.
 * @param {Blockly.Block} innerBlock - The block to be wrapped.
 * @param {Object} wrapperState - The serialized state of the wrapper block (Blockly JSON format).
 * @param {string} statementInputName - The name of the statement input on the wrapper where the inner block should be slotted.
 * @returns {Blockly.Block} The newly created wrapper block with the inner block slotted in.
 * @throws Will throw an error if the specified statement input is not found.
 */
export function wrapBlockWith(innerBlock, wrapperState, statementInputName) {
    const workspace = innerBlock.workspace;

    const prevGroupId = Blockly.Events.getGroup();
    Blockly.Events.setGroup(true);

    // Save connections before detaching
    const prevConn = innerBlock.previousConnection?.targetConnection || null;
    const nextConn = innerBlock.nextConnection?.targetConnection || null;

    // Create the wrapper block from serialized state
    const wrapperBlock = Blockly.serialization.blocks.append(wrapperState, workspace, { recordUndo: true });

    // Move wrapper to the original block's location
    const coords = innerBlock.getRelativeToSurfaceXY();
    wrapperBlock.moveBy(coords.x, coords.y);

    // Disconnect innerBlock before re-connecting
    innerBlock.previousConnection?.disconnect();
    innerBlock.nextConnection?.disconnect();

    // // Find the first statement input on the wrapper block
    // const statementInput = wrapperBlock.inputList.find(
    //     (input) =>
    //         input.connection &&
    //         input.connection.type === Blockly.NEXT_STATEMENT
    // );
    // Locate the named statement input
    const statementInput = wrapperBlock.getInput(statementInputName);

    if (!statementInput) {
        wrapperBlock.dispose(true);
        throw new Error("Wrapper block has no statement input to accept the inner block.");
    }

    // Connect inner block into the wrapper's statement input
    const statementConn = statementInput.connection;
    if (statementConn && innerBlock.previousConnection) {
        statementConn.connect(innerBlock.previousConnection);
    }

    // Reconnect the wrapper into the previous/next chain
    prevConn && prevConn.connect(wrapperBlock.previousConnection);
    nextConn && nextConn.connect(wrapperBlock.nextConnection);
    // if (prevConn && wrapperBlock.previousConnection) {
    //     prevConn.connect(wrapperBlock.previousConnection);
    // }

    // if (nextConn && wrapperBlock.nextConnection) {
    //     wrapperBlock.nextConnection.connect(nextConn);
    // }

    innerBlock.setMovable(false);
    innerBlock.setDeletable(false);
    Blockly.Events.setGroup(prevGroupId);

    return wrapperBlock;
}

/**
 * Unwraps a block by extracting the first block inside its first statement input
 * and reconnecting it to the wrapper's previous and next connections.
 * The wrapper block is then disposed.
 *
 * @param {Blockly.Block} wrapperBlock - The wrapper block to remove.
 * @returns {Blockly.Block|null} The unwrapped inner block if found, otherwise null.
 */
export function unwrapBlock(wrapperBlock) {
    // const workspace = wrapperBlock.workspace;

    // Get the wrapper's surrounding connections
    const prevConn = wrapperBlock.previousConnection?.targetConnection || null;
    const nextConn = wrapperBlock.nextConnection?.targetConnection || null;
    const prevGroupId = Blockly.Events.getGroup();
    Blockly.Events.setGroup(true);
    // Find the first statement input
    const statementInput = wrapperBlock.inputList.find(
        (input) =>
            input.connection &&
            input.connection.type === Blockly.NEXT_STATEMENT
    );

    if (!statementInput) {
        console.warn("Wrapper block has no statement input to unwrap.");
        return null;
    }

    const innerConn = statementInput.connection;
    const innerBlock = innerConn?.targetBlock() || null;

    if (!innerBlock) {
        console.warn("No block found inside the wrapper to unwrap.");
        return null;
    }

    // Disconnect inner block before moving it
    innerBlock.previousConnection?.disconnect();
    innerBlock.nextConnection?.disconnect();

    // Move inner block to wrapper's location
    const coords = wrapperBlock.getRelativeToSurfaceXY();
    innerBlock.moveBy(coords.x - innerBlock.getRelativeToSurfaceXY().x, coords.y - innerBlock.getRelativeToSurfaceXY().y);

    // Connect inner block to previous/next of wrapper
    prevConn && prevConn.connect(innerBlock.previousConnection);
    nextConn && nextConn.connect(innerBlock.nextConnection);
    // if (prevConn && innerBlock.previousConnection) {
    //     prevConn.connect(innerBlock.previousConnection);
    // }

    // if (nextConn && innerBlock.nextConnection) {
    //     innerBlock.nextConnection.connect(nextConn);
    // }

    // Remove the wrapper block from workspace
    // Blockly.Events.setRecordUndo(false);
    // wrapperBlock.unplug();
    wrapperBlock.dispose(true);
    // Blockly.Events.setRecordUndo(true);

    innerBlock.setMovable(true);
    innerBlock.setDeletable(true);

    Blockly.Events.setGroup(prevGroupId);

    return innerBlock;
}

/**
 * Creates a block state for a reusable set return value block.
 *
 * @param {string} refRbBlockId - The ID of the referenced reusable block.
 * @param {string} variableId - The ID of the variable to be set.
 * @returns {Blockly.serialization.blocks.State} The block state for the reusable block reference.
 */
export function getRbReturnValueBlockState(refRbBlockId, variableId) {
    return {
        type: "vmt_variables_set_reusable",
        data: {
            ref: {
                id: refRbBlockId,
            }
        },
        fields: {
            VAR: {
                id: variableId,
            },
        },
    };
}